<script setup lang="ts">
import {computed} from "vue";
import {isSMSBatch, MassCertificationNotificationBatch} from "@/core/interfaces/MassCertification";
import MessageSourceBadge from "@/communication/components/MessageSourceBadge.vue";
import dateHelper from "@/core/helpers/dateHelper";
import {CalendarClock, MousePointerClick, Users} from "@lucide/vue";
import NotificationTemplate from "@/notification-template/components/NotificationTemplate.vue";
import {NotificationTemplate as INotificationTemplate} from "@/notification-template/interfaces";

const props = defineProps<{
    batch: MassCertificationNotificationBatch;
}>();

const title = computed(() => {
    switch (props.batch.template?.targetable_type) {
        case "laboratory": {
            return 'Envoi depuis espace marque';
        }
        case 'program': {
            return 'Envoi depuis module';
        }
        case "survey": {
            return 'Envoi depuis questionnaire';
        }
        default: {
            return 'Envoi ciblé (manuel)';
        }
    }
});

const total = computed(() => {
    return props.batch.conversation_messages_count ?? 0;
});

const readRate = computed(() => {
    if (total.value === 0) {
        return 0;
    }

    return Math.round(((props.batch.read_conversation_messages_count ?? 0) / total.value) * 100);
});

const interactionRate = computed(() => {
    if (total.value === 0) {
        return 0;
    }

    return Math.round(((props.batch.reacted_conversation_messages_count ?? 0) / total.value) * 100);
});

const stats = computed(() => {
    return [
        {
            label: 'Destinataires',
            icon: Users,
            value: `${total.value}`,
            rate: null as number | null,
            barClass: '',
            valueClass: 'text-foreground',
        },
        ...!isSMSBatch(props.batch) ? [{
            label: 'Lus',
            icon: null,
            value: `${readRate.value}%`,
            rate: readRate.value,
            barClass: 'bg-primary',
            valueClass: 'text-primary',
        },
        {
            label: 'Interactions',
            icon: MousePointerClick,
            value: `${interactionRate.value}%`,
            rate: interactionRate.value,
            barClass: 'bg-indigo-500',
            valueClass: 'text-indigo-500',
        }] : [],
    ];
});

const template = computed<INotificationTemplate | null>(() => {
    if(props.batch.template) {
        return props.batch.template;
    }

    if(props.batch.content) {
        return {
            content: props.batch.content,
            parsed_content: props.batch.parsed_content ?? props.batch.content,
            type: 'sms'
        } as INotificationTemplate;
    }

    return null;
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-2">
      <h1 class="text-xl font-semibold">
        {{ title }}
      </h1>
      <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
        <MessageSourceBadge
          :type="batch.template?.targetable_type ?? null"
          :name="batch.template?.targetable?.name"
        />
        <span class="inline-flex items-center gap-1">
          <CalendarClock class="w-4 h-4" />
          Envoyé le {{ dateHelper.mediumFormat(batch.created_at) }}
        </span>
      </div>
    </div>

    <div v-if="template" class="border bg-muted rounded-lg p-6 flex flex-col items-center self-center">
      <NotificationTemplate :template="template" :show-name="false" class="max-w-sm" />
    </div>

    <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div
        v-for="stat in stats"
        :key="stat.label"
        class="flex flex-col gap-2 rounded-lg border bg-muted/40 p-4"
      >
        <div class="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <component :is="stat.icon" v-if="stat.icon" class="w-3.5 h-3.5" />
          {{ stat.label }}
        </div>
        <p class="text-2xl font-bold" :class="stat.valueClass">
          {{ stat.value }}
        </p>
        <div v-if="stat.rate !== null" class="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            class="h-full rounded-full transition-all"
            :class="stat.barClass"
            :style="{ width: `${stat.rate}%` }"
          />
        </div>
      </div>
    </div>
  </div>
</template>
